import { Box, Typography } from "@mui/material";
import { styles } from "../TracksPageComponents/styles";
import { Role, SubscriptionState } from "../../proto/api_pb";
import { BorderLinearProgress } from "../StorageComponent/styles";
import {
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import React, { useContext, useEffect, useState } from "react";
import { formatFileSize } from "../../services/trackService";
import { tierStorageMap } from "../../pages/Billing/types";
import { DrawerStorageProps } from "./types";
import { isBillingRequired } from "../../utils/subscriptionBillingUtils";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuthMode } from "../../hooks/useAuthMode";
import { QuotaMeter } from "../Guest";
import { SubscriptionStatusBanner } from "./SubscriptionStatusBanner";

// Small local date-diff for the trial countdown appended to the existing
// "Trial Mode" badge text — subscription_trial_end is a future timestamp so
// the shared formatRelativeDays() (built for past "N days ago" timestamps)
// doesn't fit; kept intentionally tiny/local per the design spec.
const formatDaysUntil = (timestampMs: number): string => {
  const days = Math.ceil((timestampMs - Date.now()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "in 1 day";
  return `in ${days} days`;
};

export const DrawerStorage: React.FC<DrawerStorageProps> = ({
  currentUser,
}) => {
  // All hooks must run unconditionally on every render — the guest-mode
  // short-circuit lives below the hook block so React's call order stays
  // stable when the caller flips between guest and signed-in.
  const { mode, guest } = useAuthMode();
  const { loginWithRedirect } = useAuth0();
  const [totalStorage, setTotalStorage] = useState(0);
  const { storageUsage, subscriptionTier, subscriptionState } =
    useContext(UserDetailsContext);
  const { isEnterpriseAccount, enterprise } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);

  useEffect(() => {
    if (subscriptionTier && !isEnterpriseAccount) {
      setTotalStorage(tierStorageMap[subscriptionTier].storageValue);
    } else {
      setTotalStorage(enterprise?.storageLimit ?? 0);
    }
  }, [
    subscriptionTier,
    storageUsage,
    isEnterpriseAccount,
    enterprise?.storageLimit,
  ]);

  if (mode === "guest" && guest) {
    const usedMB = Math.max(0, Math.round(guest.usedBytes / (1024 * 1024)));
    const totalMB = Math.max(
      1,
      Math.round(guest.quotaBytes / (1024 * 1024))
    );
    return (
      <Box sx={{ padding: "8px 12px" }}>
        <QuotaMeter
          usedMB={usedMB}
          totalMB={totalMB}
          onUpsell={() =>
            loginWithRedirect({
              appState: { targetUrl: "/initial-setting" },
              authorizationParams: { screen_hint: "signup" },
            })
          }
        />
      </Box>
    );
  }

  const trialEnd = currentUser?.getSubscriptionTrialEnd?.();

  return (
    <>
      {subscriptionState === SubscriptionState.SUBSCRIPTION_STATE_ON_TRIAL &&
        !isEnterpriseAccount && (
          <Box sx={styles.trialModeContainer}>
            <Typography sx={{ color: "#d28535" }}>
              {`Trial Mode${
                trialEnd ? ` · ends ${formatDaysUntil(trialEnd)}` : ""
              }`}
            </Typography>
          </Box>
        )}
      {currentUser &&
        (!isEnterpriseAccount ||
          Number(enterprise?.role) !== Role.ROLE_ARTIST) && (
          <Box>
            <SubscriptionStatusBanner subscriptionState={subscriptionState} />
            <Box sx={styles.storageDetailsContainer}>
              <Box sx={styles.cloudIconContainer}>
                <img
                  style={{ width: "20px", height: "20px" }}
                  src="/assets/icons/TracksPageIcons/cloud-blank-01.svg"
                  alt=""
                />
              </Box>
              <Box sx={{ width: "80%" }}>
                {!isBillingRequired(subscriptionTier, storageUsage) ||
                isEnterpriseAccount ? (
                  <Typography sx={{ fontSize: "14px" }}>{`${formatFileSize(
                    storageUsage
                  )} of ${formatFileSize(totalStorage)} used`}</Typography>
                ) : (
                  <Typography
                    sx={{ fontSize: "14px" }}
                  >{`Upgrade your subscription`}</Typography>
                )}

                <BorderLinearProgress
                  variant="determinate"
                  value={Math.min((storageUsage / totalStorage) * 100, 100)}
                  sx={{ marginTop: 1 }}
                />
              </Box>
            </Box>
          </Box>
        )}
    </>
  );
};
